/**
 * Content Relevance Matching Algorithm
 *
 * This utility provides intelligent content matching between blog posts,
 * knowledge articles, and pSEO pages based on semantic relevance.
 *
 * The algorithm uses multiple signals:
 * - Title token overlap
 * - Keyword/tag matching
 * - Category alignment
 * - Description similarity
 * - Word frequency analysis
 */

export interface ContentItem {
  slug: string;
  title: string;
  description: string;
  keywords?: string[];
  tags?: string[];
  category?: string;
}

export interface RelevanceScore {
  slug: string;
  score: number;
  reasons: string[];
}

/**
 * Normalize text for comparison: lowercase, remove punctuation, trim
 */
function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Extract meaningful tokens from text (remove common stop words)
 */
function extractTokens(text: string): string[] {
  const stopWords = new Set([
    'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
    'of', 'with', 'by', 'from', 'as', 'is', 'was', 'are', 'be', 'been',
    'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'should',
    'can', 'could', 'may', 'might', 'must', 'this', 'that', 'these', 'those',
    'i', 'you', 'he', 'she', 'it', 'we', 'they', 'what', 'which', 'who',
    'when', 'where', 'why', 'how', 'all', 'each', 'every', 'both', 'few',
    'more', 'most', 'other', 'some', 'such', 'no', 'nor', 'not', 'only',
    'own', 'same', 'so', 'than', 'too', 'very', 'just', 'about',
  ]);

  const normalized = normalizeText(text);
  return normalized
    .split(/\s+/)
    .filter(token => token.length > 2 && !stopWords.has(token));
}

/**
 * Calculate Jaccard similarity between two sets of tokens
 */
function jaccardSimilarity(set1: Set<string>, set2: Set<string>): number {
  const intersection = new Set([...set1].filter(x => set2.has(x)));
  const union = new Set([...set1, ...set2]);

  if (union.size === 0) return 0;
  return intersection.size / union.size;
}

/**
 * Calculate TF-IDF weighted similarity
 */
function calculateWeightedSimilarity(
  tokens1: string[],
  tokens2: string[],
  allTokens: string[]
): number {
  // Calculate term frequency
  const tf1 = new Map<string, number>();
  const tf2 = new Map<string, number>();

  tokens1.forEach(token => tf1.set(token, (tf1.get(token) || 0) + 1));
  tokens2.forEach(token => tf2.set(token, (tf2.get(token) || 0) + 1));

  // Calculate document frequency
  const df = new Map<string, number>();
  allTokens.forEach(token => df.set(token, (df.get(token) || 0) + 1));

  // Calculate cosine similarity with TF-IDF weighting
  const uniqueTokens = new Set([...tokens1, ...tokens2]);
  let dotProduct = 0;
  let magnitude1 = 0;
  let magnitude2 = 0;

  uniqueTokens.forEach(token => {
    const idf = Math.log(allTokens.length / (df.get(token) || 1));
    const weight1 = (tf1.get(token) || 0) * idf;
    const weight2 = (tf2.get(token) || 0) * idf;

    dotProduct += weight1 * weight2;
    magnitude1 += weight1 * weight1;
    magnitude2 += weight2 * weight2;
  });

  if (magnitude1 === 0 || magnitude2 === 0) return 0;
  return dotProduct / (Math.sqrt(magnitude1) * Math.sqrt(magnitude2));
}

/**
 * Calculate relevance score between two content items
 */
export function calculateRelevance(
  source: ContentItem,
  target: ContentItem,
  allContent: ContentItem[]
): RelevanceScore {
  let score = 0;
  const reasons: string[] = [];

  // 1. Title similarity (weight: 40%)
  const sourceTitleTokens = extractTokens(source.title);
  const targetTitleTokens = extractTokens(target.title);
  const titleSimilarity = jaccardSimilarity(
    new Set(sourceTitleTokens),
    new Set(targetTitleTokens)
  );

  if (titleSimilarity > 0) {
    score += titleSimilarity * 40;
    if (titleSimilarity > 0.3) {
      reasons.push(`Title similarity: ${(titleSimilarity * 100).toFixed(0)}%`);
    }
  }

  // 2. Keyword/Tag overlap (weight: 30%)
  const sourceKeywords = new Set([
    ...(source.keywords || []).map(k => normalizeText(k)),
    ...(source.tags || []).map(t => normalizeText(t)),
  ]);
  const targetKeywords = new Set([
    ...(target.keywords || []).map(k => normalizeText(k)),
    ...(target.tags || []).map(t => normalizeText(t)),
  ]);

  const keywordSimilarity = jaccardSimilarity(sourceKeywords, targetKeywords);
  if (keywordSimilarity > 0) {
    score += keywordSimilarity * 30;
    if (keywordSimilarity > 0.2) {
      reasons.push(`Keyword overlap: ${(keywordSimilarity * 100).toFixed(0)}%`);
    }
  }

  // 3. Category match (weight: 15%)
  if (source.category && target.category && source.category === target.category) {
    score += 15;
    reasons.push('Same category');
  }

  // 4. Description similarity (weight: 15%)
  const sourceDescTokens = extractTokens(source.description);
  const targetDescTokens = extractTokens(target.description);

  // Collect all tokens from all content for TF-IDF
  const allTokens = allContent.flatMap(item =>
    extractTokens(item.title + ' ' + item.description)
  );

  const descSimilarity = calculateWeightedSimilarity(
    sourceDescTokens,
    targetDescTokens,
    allTokens
  );

  if (descSimilarity > 0) {
    score += descSimilarity * 15;
    if (descSimilarity > 0.2) {
      reasons.push(`Content similarity: ${(descSimilarity * 100).toFixed(0)}%`);
    }
  }

  return {
    slug: target.slug,
    score: Math.min(score, 100), // Cap at 100
    reasons,
  };
}

/**
 * Find the top N most relevant content items
 */
export function findTopRelevantContent(
  source: ContentItem,
  candidates: ContentItem[],
  options: {
    limit?: number;
    minScore?: number;
    excludeSlugs?: string[];
  } = {}
): RelevanceScore[] {
  const {
    limit = 3,
    minScore = 10,
    excludeSlugs = [],
  } = options;

  // Filter out excluded slugs
  const filteredCandidates = candidates.filter(
    c => c.slug !== source.slug && !excludeSlugs.includes(c.slug)
  );

  // Calculate relevance scores
  const scores = filteredCandidates.map(candidate =>
    calculateRelevance(source, candidate, candidates)
  );

  // Sort by score descending and apply filters
  return scores
    .filter(s => s.score >= minScore)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

/**
 * Create a ContentItem from a blog post
 */
export function blogPostToContentItem(post: {
  slug: string;
  title: string;
  description: string;
  keywords?: string[];
  tags?: string[];
  category?: string;
}): ContentItem {
  return {
    slug: post.slug,
    title: post.title,
    description: post.description,
    keywords: post.keywords || [],
    tags: post.tags || [],
    category: post.category,
  };
}

/**
 * Create a ContentItem from a knowledge article
 */
export function knowledgeArticleToContentItem(
  article: {
    slug: string;
    title: string;
    metaDescription: string;
  },
  keywords: string[] = []
): ContentItem {
  return {
    slug: article.slug,
    title: article.title,
    description: article.metaDescription,
    keywords,
    tags: [],
    category: undefined,
  };
}

/**
 * Predefined keyword mappings for knowledge articles
 * These help improve relevance matching since knowledge articles don't have explicit keywords
 */
export const KNOWLEDGE_ARTICLE_KEYWORDS: Record<string, string[]> = {
  'what-are-ai-watermarks': [
    'ai watermarks',
    'watermarking',
    'invisible characters',
    'detection',
    'chatgpt',
    'openai',
    'unicode',
    'zero-width',
  ],
  'watermark-removal-vs-ai-detection': [
    'watermark removal',
    'ai detection',
    'turnitin',
    'gptzero',
    'detection tools',
    'plagiarism',
    'academic',
  ],
  'token-distribution-ai-watermarking': [
    'token distribution',
    'watermarking algorithm',
    'technical',
    'implementation',
    'cryptography',
    'hashing',
  ],
  'why-ai-detectors-fail': [
    'ai detection',
    'false positives',
    'accuracy',
    'limitations',
    'detection failure',
    'reliability',
  ],
  'why-short-texts-break-detection': [
    'short text',
    'detection limits',
    'text length',
    'watermark density',
    'minimum length',
  ],
};

/**
 * Predefined mappings for pSEO pages to relevant content
 * This provides manual overrides for important commercial pages
 */
export const PSEO_CONTENT_MAPPINGS: Record<string, {
  knowledgeArticles: string[];
  blogPosts: string[];
}> = {
  'chatgpt-watermark': {
    knowledgeArticles: [
      'what-are-ai-watermarks',
      'watermark-removal-vs-ai-detection',
    ],
    blogPosts: [
      'does-chatgpt-watermark-text',
    ],
  },
  'gemini-watermark': {
    knowledgeArticles: [
      'what-are-ai-watermarks',
      'token-distribution-ai-watermarking',
    ],
    blogPosts: [
      'ai-text-watermarks-explained',
      'can-turnitin-detect-chatgpt',
    ],
  },
  'claude-watermark': {
    knowledgeArticles: [
      'what-are-ai-watermarks',
      'watermark-removal-vs-ai-detection',
    ],
    blogPosts: [
      'ai-text-watermarks-explained',
      'invisible-chatgpt-watermarks',
    ],
  },
  'student-watermark': {
    knowledgeArticles: [
      'watermark-removal-vs-ai-detection',
      'why-ai-detectors-fail',
    ],
    blogPosts: [
      'ai-watermarks-academic-integrity',
      'can-turnitin-detect-chatgpt',
    ],
  },
  'free-chatgpt-watermark': {
    knowledgeArticles: [
      'what-are-ai-watermarks',
      'watermark-removal-vs-ai-detection',
    ],
    blogPosts: [
      'does-chatgpt-watermark-text',
    ],
  },
  'free-ai-text-watermark': {
    knowledgeArticles: [
      'what-are-ai-watermarks',
      'token-distribution-ai-watermarking',
    ],
    blogPosts: [
      'ai-text-watermarks-explained',
      'can-turnitin-detect-chatgpt',
    ],
  },
  'free-ai-watermark': {
    knowledgeArticles: [
      'what-are-ai-watermarks',
      'why-ai-detectors-fail',
    ],
    blogPosts: [
      'ai-text-watermarks-explained',
      'invisible-chatgpt-watermarks',
    ],
  },
  'free-gpt-watermark': {
    knowledgeArticles: [
      'what-are-ai-watermarks',
      'watermark-removal-vs-ai-detection',
    ],
    blogPosts: [
      'does-chatgpt-watermark-text',
    ],
  },
  'detect-ai-watermarks': {
    knowledgeArticles: [
      'what-are-ai-watermarks',
      'token-distribution-ai-watermarking',
    ],
    blogPosts: [
      'ai-text-watermarks-explained',
      'invisible-chatgpt-watermarks',
    ],
  },
};
